import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  GitBranch, 
  Database, 
  ArrowRight, 
  Play, 
  Pause, 
  Settings, 
  BarChart3, 
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';

interface PipelineNode {
  id: string;
  type: 'source' | 'transform' | 'destination';
  name: string;
  status: 'idle' | 'running' | 'completed' | 'error';
  progress: number;
}

const DataPipelineVisualizer = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [nodes, setNodes] = useState<PipelineNode[]>([
    { id: '1', type: 'source', name: 'SQL Database', status: 'idle', progress: 0 },
    { id: '2', type: 'transform', name: 'Data Cleaning', status: 'idle', progress: 0 },
    { id: '3', type: 'transform', name: 'Feature Engineering', status: 'idle', progress: 0 },
    { id: '4', type: 'destination', name: 'Data Warehouse', status: 'idle', progress: 0 }
  ]);

  const runPipeline = useCallback(async () => {
    setIsRunning(true);
    
    for (let i = 0; i < nodes.length; i++) {
      // Set current node to running
      setNodes(prev => prev.map((node, index) => 
        index === i ? { ...node, status: 'running', progress: 0 } : node
      ));

      // Simulate progress
      for (let progress = 0; progress <= 100; progress += 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        setNodes(prev => prev.map((node, index) => 
          index === i ? { ...node, progress } : node
        ));
      }

      // Mark as completed
      setNodes(prev => prev.map((node, index) => 
        index === i ? { ...node, status: 'completed', progress: 100 } : node
      ));
    }

    setIsRunning(false);
  }, [nodes.length]);

  const resetPipeline = () => {
    setNodes(prev => prev.map(node => ({ ...node, status: 'idle', progress: 0 })));
    setIsRunning(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running': return <Clock className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error': return <AlertCircle className="w-4 h-4 text-red-500" />;
      default: return <GitBranch className="w-4 h-4 text-gray-400" />;
    }
  };

  const getNodeColor = (type: string) => {
    switch (type) {
      case 'source': return 'from-blue-500 to-blue-600';
      case 'transform': return 'from-purple-500 to-purple-600';
      case 'destination': return 'from-green-500 to-green-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            Data Pipeline Visualizer
          </h1>
          <p className="text-blue-200 text-lg">
            Interactive ETL pipeline designer with Azure Data Factory integration
          </p>
        </div>

        {/* Controls */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={runPipeline}
            disabled={isRunning}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Play className="w-5 h-5" />
            {isRunning ? 'Running...' : 'Run Pipeline'}
          </button>
          
          <button
            onClick={resetPipeline}
            disabled={isRunning}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg hover:from-gray-600 hover:to-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Settings className="w-5 h-5" />
            Reset
          </button>
        </div>

        {/* Pipeline Visualization */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-8">
          <div className="flex items-center justify-between">
            {nodes.map((node, index) => (
              <div key={node.id} className="flex items-center">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: index * 0.2 }}
                  className="flex flex-col items-center"
                >
                  <div className={`w-32 h-20 bg-gradient-to-r ${getNodeColor(node.type)} rounded-lg flex flex-col items-center justify-center text-white shadow-lg`}>
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusIcon(node.status)}
                      <Database className="w-5 h-5" />
                    </div>
                    <div className="text-sm font-medium text-center">{node.name}</div>
                  </div>
                  
                  {/* Progress Bar */}
                  {node.status === 'running' && (
                    <div className="w-full bg-gray-300 rounded-full h-2 mt-2">
                      <motion.div
                        className="bg-blue-500 h-2 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${node.progress}%` }}
                        transition={{ duration: 0.1 }}
                      />
                    </div>
                  )}
                  
                  <div className="text-xs text-blue-200 mt-2 capitalize">{node.type}</div>
                </motion.div>

                {/* Arrow between nodes */}
                {index < nodes.length - 1 && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.2 + 0.1 }}
                    className="mx-6"
                  >
                    <ArrowRight className="w-8 h-8 text-blue-300" />
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Monitoring Dashboard */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <BarChart3 className="w-6 h-6 text-blue-400" />
              <h3 className="text-lg font-semibold text-white">Pipeline Metrics</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-blue-200">Total Records</span>
                <span className="text-white font-mono">1,234,567</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-200">Processing Rate</span>
                <span className="text-white font-mono">15.2k/sec</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-200">Success Rate</span>
                <span className="text-green-400 font-mono">99.8%</span>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-6 h-6 text-purple-400" />
              <h3 className="text-lg font-semibold text-white">Performance</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-blue-200">Avg Latency</span>
                <span className="text-white font-mono">125ms</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-200">Throughput</span>
                <span className="text-white font-mono">2.5 GB/min</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-200">CPU Usage</span>
                <span className="text-yellow-400 font-mono">67%</span>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-orange-400" />
              <h3 className="text-lg font-semibold text-white">Status</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-blue-200">Pipeline State</span>
                <span className={`font-mono ${isRunning ? 'text-blue-400' : 'text-green-400'}`}>
                  {isRunning ? 'Running' : 'Idle'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-200">Last Run</span>
                <span className="text-white font-mono">2 min ago</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-200">Next Scheduled</span>
                <span className="text-white font-mono">58 min</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataPipelineVisualizer;
